import { useState } from "react";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { useLingui } from "@lingui/react";
import { useSegment } from "src/hooks/useSegment";
import FORM_ERROR_MESSAGES from "src/messages/form";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import { SignLocation } from "src/tracking/segment";
import { SKIP_SIGN_IN_MESSAGE } from "src/utils/constants";
import { signIn } from "src/utils/nextAuth";
import prepareNextAuthErrorMessage from "src/utils/prepareNextAuthErrorMessage";

interface CreateAccountFormValues {
  email: string;
  password: string;
}

type SubmitResult = { created: true } | { created: false; message?: string };

interface UseCreateAccountFormArgs {
  name?: string;
}

interface UseCreateAccountForm {
  formik: FormikProps<CreateAccountFormValues>;
  submitted: boolean;
  failed: boolean;
  errorMessage: string | undefined;
}

function useCreateAccountForm({
  name,
}: UseCreateAccountFormArgs = {}): UseCreateAccountForm {
  const { i18n } = useLingui();
  const { segmentCheckoutSignedUp } = useSegment();

  const [result, setResult] = useState<SubmitResult | undefined>(undefined);

  const formik: FormikProps<CreateAccountFormValues> =
    useFormik<CreateAccountFormValues>({
      initialValues: {
        email: "",
        password: "",
      },
      validationSchema: Yup.object().shape({
        email: Yup.string()
          .required(FORM_ERROR_MESSAGES.emailRequired)
          .email(FORM_ERROR_MESSAGES.email),
        password: Yup.string()
          .required(FORM_ERROR_MESSAGES.passwordRequired)
          .min(6, FORM_ERROR_MESSAGES.passwordLength)
          .matches(/^\S+$/, FORM_ERROR_MESSAGES.passwordSpace),
      }),
      onSubmit: async ({ email, password }) => {
        setResult(undefined);

        const trimmedEmail = email.trim();

        try {
          const response = await signIn(AuthProvider.CREDENTIALS_REGISTER, {
            redirect: false,
            ...(name ? { name } : {}),
            email: trimmedEmail,
            password,
            skipSignIn: true,
          });

          if (response?.error === SKIP_SIGN_IN_MESSAGE.WITH_TRACKING) {
            setResult({ created: true });
            void segmentCheckoutSignedUp({
              email: trimmedEmail,
              location: SignLocation.CONFIRMATION_PAGE,
            });
            return;
          }

          setResult({
            created: false,
            message:
              response?.error &&
              response.error !== SKIP_SIGN_IN_MESSAGE.WITHOUT_TRACKING
                ? prepareNextAuthErrorMessage(response.error, i18n)
                : undefined,
          });
        } catch (error) {
          console.error(
            "An error occurred while registering the account.",
            error
          );
          setResult({ created: false });
        }
      },
    });

  return {
    formik,
    submitted: result?.created === true,
    failed: result?.created === false,
    errorMessage: result?.created === false ? result.message : undefined,
  };
}

export { useCreateAccountForm };
export type { CreateAccountFormValues };
